import type { RuleResponse, KnowledgeItemResponse, GuardrailItemResponse } from "../../gen/models";
import {
  BarChart2,
  Sparkles,
  ShieldCheck,
  Bot,
  type LucideIcon,
} from "lucide-react";

export interface ResolvedRule {
  id?: string;
  category: string;
  title: string;
  instruction?: string;
  status?: string;
  priority?: number;
  isResolved: boolean;
}

export interface ResolvedGuardrail {
  id?: string;
  category: string;
  title: string;
  description?: string | null;
  similarity_score?: number;
  anchor_text?: string;
  action?: string;
  severity?: string;
  on_block_action?: string;
  status?: string;
  isResolved: boolean;
}

export interface ResolvedSourceGroup {
  id?: string;
  title: string;
  category: string;
  isTool?: boolean;
  toolKey?: string;
  isResolved: boolean;
  chunks: string[];
}

export interface SourcesGroupResult {
  groups: ResolvedSourceGroup[];
  totalChunks: number;
  knowledgeItemCount: number;
  toolCount: number;
}

export interface AgentInfo {
  name: string;
  role: string;
  icon: LucideIcon;
  color: string;
  placeholder: string;
}

import { sanitizeProtocoloName } from "../../hooks/useUserDirectory";

export function getAgentInfo(agentType?: string, agentName?: string): AgentInfo {
  const cleanType = (agentType || "").trim().toLowerCase();
  const cleanCustomName = agentName ? sanitizeProtocoloName(agentName, "Sofia") : undefined;

  switch (cleanType) {
    case "biblioteca":
      return {
        name: cleanCustomName || "Analytics",
        role: "Analytics Lead",
        icon: BarChart2,
        color: "text-indigo-400 bg-stone-900",
        placeholder: "Ask about marketing metrics, leads pipeline, or booking analytics...",
      };
    case "sales":
      return {
        name: cleanCustomName || "Quotes",
        role: "Sales Concierge",
        icon: Sparkles,
        color: "text-emerald-400 bg-stone-900",
        placeholder: "Inquire about stay dates, residence options, or luxury pricing quotes...",
      };
    case "frontdesk":
    case "el_protocolo":
    case "protocolo":
      return {
        name: cleanCustomName || "Sofia",
        role: "Front Desk Concierge",
        icon: ShieldCheck,
        color: "text-amber-400 bg-stone-900",
        placeholder: "Message front desk concierge simulation...",
      };
    case "feedback":
      return {
        name: cleanCustomName || "Feedback",
        role: "Feedback & Curation",
        icon: ShieldCheck,
        color: "text-cyan-400 bg-stone-900",
        placeholder: "Message feedback & curation agent...",
      };
    default:
      return {
        name: cleanCustomName || (cleanType ? cleanType : "Silvestre Agent"),
        role: "Agent",
        icon: Bot,
        color: "text-emerald-400 bg-stone-900",
        placeholder: "Send message turn to agent...",
      };
  }
}

export function extractListItems<T = any>(source: any): T[] {
  if (!source) return [];
  const d = source?.data !== undefined ? source.data : source;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.rules)) return d.rules;
  if (Array.isArray(d?.batches)) return d.batches;
  if (Array.isArray(d?.comments)) return d.comments;
  if (Array.isArray(d?.evaluations)) return d.evaluations;
  if (Array.isArray(d?.runs)) return d.runs;
  return [];
}

export function createGovernanceLookups(
  rulesList: RuleResponse[],
  knowledgeList: KnowledgeItemResponse[],
  guardrailsList?: GuardrailItemResponse[]
) {
  const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

  const safeRules = extractListItems<RuleResponse>(rulesList);
  const safeKnowledge = extractListItems<KnowledgeItemResponse>(knowledgeList);
  const safeGuardrails = extractListItems<GuardrailItemResponse>(guardrailsList);

  const byId = new Map<string, RuleResponse>();
  const byNormalizedTitle = new Map<string, RuleResponse>();
  const byTag = new Map<string, RuleResponse>();

  safeRules.forEach((r) => {
    byId.set(r.id, r);
    byNormalizedTitle.set(normalize(r.title), r);
    byTag.set(normalize(`[${r.category}] ${r.title}`), r);
  });

  const kById = new Map<string, KnowledgeItemResponse>();
  const kByTitle = new Map<string, KnowledgeItemResponse>();
  const kByCategory = new Map<string, KnowledgeItemResponse>();

  safeKnowledge.forEach((k) => {
    kById.set(k.id, k);
    kByTitle.set(normalize(k.title), k);
    kByCategory.set(normalize(k.category), k);
  });

  const gById = new Map<string, GuardrailItemResponse>();
  const gByNormalizedTitle = new Map<string, GuardrailItemResponse>();
  const gByTag = new Map<string, GuardrailItemResponse>();

  safeGuardrails.forEach((g) => {
    gById.set(g.id, g);
    gByNormalizedTitle.set(normalize(g.title), g);
    gByTag.set(normalize(`[${g.category}] ${g.title}`), g);
  });

  const resolveRule = (raw: any): ResolvedRule => {
    if (!raw) return { category: "rule", title: "Unknown Rule", isResolved: false };

    if (typeof raw === "object") {
      const existing =
        (raw.id ? byId.get(raw.id) : undefined) ||
        (raw.title ? byNormalizedTitle.get(normalize(raw.title)) : undefined);

      const resolvedId =
        existing?.id ||
        (raw.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw.id)
          ? raw.id
          : undefined);

      return {
        id: resolvedId,
        category: raw.category || existing?.category || "operational",
        title: raw.title || existing?.title || "Operational Rule",
        instruction: raw.instruction || existing?.instruction,
        status: raw.status || existing?.status,
        priority: raw.priority ?? existing?.priority,
        isResolved: Boolean(resolvedId),
      };
    }

    const rawStr = String(raw).trim();
    if (byId.has(rawStr)) {
      const r = byId.get(rawStr)!;
      return {
        id: r.id,
        category: r.category,
        title: r.title,
        instruction: r.instruction,
        status: r.status,
        priority: r.priority,
        isResolved: true,
      };
    }

    const norm = normalize(rawStr);
    if (byNormalizedTitle.has(norm)) {
      const r = byNormalizedTitle.get(norm)!;
      return {
        id: r.id,
        category: r.category,
        title: r.title,
        instruction: r.instruction,
        status: r.status,
        priority: r.priority,
        isResolved: true,
      };
    }

    if (byTag.has(norm)) {
      const r = byTag.get(norm)!;
      return {
        id: r.id,
        category: r.category,
        title: r.title,
        instruction: r.instruction,
        status: r.status,
        priority: r.priority,
        isResolved: true,
      };
    }

    for (const [tagNorm, r] of byTag.entries()) {
      if (norm.includes(tagNorm) || tagNorm.includes(norm)) {
        return {
          id: r.id,
          category: r.category,
          title: r.title,
          instruction: r.instruction,
          status: r.status,
          priority: r.priority,
          isResolved: true,
        };
      }
    }

    return { category: "rule", title: rawStr, isResolved: false };
  };

  const canonicalizeTool = (str: string): { title: string; category: string; key: string } => {
    const s = str
      .trim()
      .toLowerCase()
      .replace(/^tool:\s*/i, "")
      .replace(/[\s_-]*(tool|capability|function)$/i, "")
      .replace(/\(\)$/, "")
      .trim();

    const snake = s.replace(/[\s-]+/g, "_");

    if (
      snake === "get_weather" ||
      snake === "weather" ||
      snake === "land_weather" ||
      snake === "get_land_weather"
    ) {
      return { title: "get_weather", category: "weather", key: "get_weather" };
    }
    if (
      snake === "get_marine_weather" ||
      snake === "marine_weather" ||
      snake === "marine_surf" ||
      snake === "surf_weather"
    ) {
      return { title: "get_marine_weather", category: "weather", key: "get_marine_weather" };
    }
    if (snake === "search_knowledge" || snake === "knowledge_search" || snake === "search") {
      return { title: "search_knowledge", category: "knowledge", key: "search_knowledge" };
    }
    if (
      snake === "check_live_rates" ||
      snake === "mirai_rates" ||
      snake === "mirai_live_rates" ||
      snake.includes("live_rates") ||
      s.includes("live rates")
    ) {
      return { title: "Mirai Live Rates Engine", category: "live_rates", key: "check_live_rates" };
    }
    if (snake === "execute_sql" || snake === "read_query" || snake === "run_query") {
      return { title: "execute_sql", category: "analytics", key: "execute_sql" };
    }

    return {
      title: snake || str.trim(),
      category: "tool",
      key: snake || str.trim().toLowerCase(),
    };
  };

  const isToolIdentifier = (str: string): boolean => {
    const s = str
      .trim()
      .toLowerCase()
      .replace(/^tool:\s*/i, "")
      .replace(/[\s_-]*(tool|capability|function)$/i, "")
      .replace(/\(\)$/, "")
      .trim();
    const snake = s.replace(/[\s-]+/g, "_");

    if (
      snake === "get_weather" ||
      snake === "get_marine_weather" ||
      snake === "check_live_rates" ||
      snake === "mirai_rates" ||
      snake === "search_knowledge" ||
      snake === "execute_sql" ||
      snake === "read_query" ||
      snake === "run_query"
    ) {
      return true;
    }
    if (str.trim().toLowerCase().startsWith("tool:") || str.trim().toLowerCase().endsWith("tool")) {
      return true;
    }
    if (
      /^(get|search|check|fetch|calculate|execute|query|run|create|update|delete|send|lookup)_[a-z0-9_]+$/.test(
        snake
      )
    ) {
      return true;
    }
    if (/^[a-z][a-z0-9]*(_[a-z0-9]+)+$/.test(snake)) {
      return true;
    }
    return false;
  };

  const resolveSource = (
    raw: any
  ): {
    id?: string;
    title: string;
    category?: string;
    isTool?: boolean;
    toolKey?: string;
    isResolved: boolean;
    chunkId?: string;
  } => {
    if (!raw) return { title: "Unknown Source", isResolved: false };

    if (typeof raw === "object") {
      const explicitTool = Boolean(
        raw.isTool ||
          raw.is_tool ||
          raw.category === "tool" ||
          raw.category === "live_rates" ||
          raw.type === "tool" ||
          raw.tool_name ||
          raw.tool
      );
      const isLiveRates =
        raw.category === "live_rates" ||
        String(raw.title || "").includes("Mirai Live Rates") ||
        String(raw.title || "").toLowerCase().includes("live_rates");
      const nameOrTitle = String(
        raw.title || raw.tool_name || raw.tool || raw.name || ""
      ).trim();
      const isToolName = isToolIdentifier(nameOrTitle);

      if (explicitTool || isLiveRates || isToolName) {
        const canonical = canonicalizeTool(nameOrTitle || (isLiveRates ? "check_live_rates" : "tool"));
        return {
          id: raw.id,
          title: canonical.title,
          category: raw.category || canonical.category,
          isTool: true,
          toolKey: canonical.key,
          isResolved: true,
        };
      }

      const existing =
        (raw.id ? kById.get(raw.id) : undefined) ||
        (raw.title ? kByTitle.get(normalize(raw.title)) : undefined);

      const isDbt = raw.category === "dbt Model" || String(raw.id || "").startsWith("analytics.");
      const resolvedId =
        existing?.id ||
        (!isDbt &&
        raw.id &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw.id)
          ? raw.id
          : undefined);

      const chunkId =
        raw.chunk_id && typeof raw.chunk_id === "string" ? raw.chunk_id : undefined;

      return {
        id: resolvedId,
        title: raw.title || existing?.title || "Knowledge Document",
        category: raw.category || existing?.category || (isDbt ? "dbt Model" : "general"),
        isTool: false,
        isResolved: Boolean(resolvedId || isDbt),
        chunkId,
      };
    }

    const rawStr = String(raw).trim();

    if (kById.has(rawStr)) {
      const k = kById.get(rawStr)!;
      return {
        id: k.id,
        title: k.title,
        category: k.category,
        isTool: false,
        isResolved: true,
      };
    }

    const norm = normalize(rawStr);
    if (kByTitle.has(norm)) {
      const k = kByTitle.get(norm)!;
      return {
        id: k.id,
        title: k.title,
        category: k.category,
        isTool: false,
        isResolved: true,
      };
    }

    if (isToolIdentifier(rawStr)) {
      const canonical = canonicalizeTool(rawStr);
      return {
        title: canonical.title,
        category: canonical.category,
        isTool: true,
        toolKey: canonical.key,
        isResolved: true,
      };
    }

    return { title: rawStr, category: "general", isTool: false, isResolved: false };
  };

  const deduplicateRules = (rawRules: any[]): ResolvedRule[] => {
    const map = new Map<string, ResolvedRule>();
    for (const raw of rawRules) {
      if (!raw) continue;
      const rule = resolveRule(raw);
      const key = rule.id
        ? `id:${rule.id}`
        : `title:${rule.category}:${normalize(rule.title)}`;
      if (!map.has(key)) {
        map.set(key, rule);
      }
    }
    return Array.from(map.values());
  };

  const deduplicateAndGroupSources = (rawSources: any[]): SourcesGroupResult => {
    const map = new Map<string, ResolvedSourceGroup>();
    let totalChunks = 0;

    for (const raw of rawSources) {
      if (!raw) continue;
      const res = resolveSource(raw);
      const key = res.isTool
        ? `tool:${res.toolKey || canonicalizeTool(res.title).key}`
        : res.id
        ? `id:${res.id}`
        : `title:${normalize(res.title)}`;

      if (!map.has(key)) {
        const itemChunks = res.chunkId ? [res.chunkId] : [];
        if (res.chunkId) totalChunks += 1;
        map.set(key, {
          id: res.id,
          title: res.title,
          category: res.category || "general",
          isTool: res.isTool,
          toolKey: res.toolKey,
          isResolved: res.isResolved,
          chunks: itemChunks,
        });
      } else {
        const existing = map.get(key)!;
        if (res.chunkId && !existing.chunks.includes(res.chunkId)) {
          existing.chunks.push(res.chunkId);
          totalChunks += 1;
        }
        if (res.isTool && res.title.includes("_") && !existing.title.includes("_")) {
          existing.title = res.title;
        }
      }
    }

    const groups = Array.from(map.values());
    const toolCount = groups.filter((g) => g.isTool).length;
    const knowledgeItemCount = groups.filter((g) => !g.isTool).length;

    return { groups, totalChunks, knowledgeItemCount, toolCount };
  };

  const resolveGuardrail = (raw: any): ResolvedGuardrail => {
    if (!raw) return { category: "safety", title: "Guardrail Policy", isResolved: false };

    if (typeof raw === "object") {
      const existing =
        (raw.id ? gById.get(raw.id) : undefined) ||
        (raw.title ? gByNormalizedTitle.get(normalize(raw.title)) : undefined);

      const resolvedId =
        existing?.id ||
        (raw.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw.id)
          ? raw.id
          : undefined);

      return {
        id: resolvedId,
        category: raw.category || existing?.category || "safety",
        title: raw.title || existing?.title || (raw.category ? `Guardrail: ${raw.category}` : "Guardrail Policy"),
        description: raw.description || existing?.description,
        similarity_score: raw.similarity_score,
        anchor_text: raw.anchor_text,
        action: raw.action || existing?.action || "block",
        severity: raw.severity || existing?.severity,
        on_block_action: raw.on_block_action,
        status: raw.status || existing?.status,
        isResolved: Boolean(resolvedId),
      };
    }

    const rawStr = String(raw).trim();
    if (gById.has(rawStr)) {
      const g = gById.get(rawStr)!;
      return {
        id: g.id,
        category: g.category,
        title: g.title,
        description: g.description,
        action: g.action,
        severity: g.severity,
        status: g.status,
        isResolved: true,
      };
    }

    const norm = normalize(rawStr);
    if (gByNormalizedTitle.has(norm)) {
      const g = gByNormalizedTitle.get(norm)!;
      return {
        id: g.id,
        category: g.category,
        title: g.title,
        description: g.description,
        action: g.action,
        severity: g.severity,
        status: g.status,
        isResolved: true,
      };
    }

    if (gByTag.has(norm)) {
      const g = gByTag.get(norm)!;
      return {
        id: g.id,
        category: g.category,
        title: g.title,
        description: g.description,
        action: g.action,
        severity: g.severity,
        status: g.status,
        isResolved: true,
      };
    }

    const match = rawStr.match(/^\[([^\]]+)\]\s*(.*)$/);
    if (match) {
      const cat = match[1].toLowerCase();
      const title = match[2];
      return {
        category: cat,
        title: title || rawStr,
        isResolved: false,
      };
    }

    return {
      category: "safety",
      title: rawStr,
      isResolved: false,
    };
  };

  const deduplicateGuardrails = (rawGuardrails: any[]): ResolvedGuardrail[] => {
    const map = new Map<string, ResolvedGuardrail>();
    for (const raw of rawGuardrails) {
      if (!raw) continue;
      const g = resolveGuardrail(raw);
      const key = g.id
        ? `id:${g.id}`
        : `title:${g.category}:${normalize(g.title)}`;
      if (!map.has(key)) {
        map.set(key, g);
      }
    }
    return Array.from(map.values());
  };

  return {
    deduplicateRules,
    deduplicateAndGroupSources,
    deduplicateGuardrails,
  };
}

export function extractThreadGovernanceItems(
  messages: any[],
  rulesList: RuleResponse[],
  knowledgeList: KnowledgeItemResponse[],
  guardrailsList?: GuardrailItemResponse[]
): {
  referencedRules: RuleResponse[];
  referencedKnowledge: KnowledgeItemResponse[];
  referencedGuardrails: GuardrailItemResponse[];
} {
  const safeMessages = Array.isArray(messages)
    ? messages
    : (messages as any)?.messages || (messages as any)?.items || [];
  const safeRules = extractListItems<RuleResponse>(rulesList);
  const safeKnowledge = extractListItems<KnowledgeItemResponse>(knowledgeList);
  const safeGuardrails = extractListItems<GuardrailItemResponse>(guardrailsList);

  const { deduplicateRules, deduplicateAndGroupSources, deduplicateGuardrails } = createGovernanceLookups(
    safeRules,
    safeKnowledge,
    safeGuardrails
  );

  const rawRulesList: any[] = [];
  const rawSourcesList: any[] = [];
  const rawGuardrailsList: any[] = [];

  for (const msg of safeMessages) {
    const metadata = msg.metadata || {};
    const quoteDraft = metadata.quote_draft;
    const isGuardrailBlocked = Boolean(
      metadata.guardrail_blocked ||
      (metadata.status === "blocked" && (metadata.category || metadata.guardrails_applied))
    );

    if (!isGuardrailBlocked) {
      if (Array.isArray(quoteDraft?.rules_applied_details)) rawRulesList.push(...quoteDraft.rules_applied_details);
      if (Array.isArray(metadata.draft?.rules_applied_details)) rawRulesList.push(...metadata.draft.rules_applied_details);
      if (Array.isArray(metadata.rules_applied_details)) rawRulesList.push(...metadata.rules_applied_details);
      if (Array.isArray(metadata.rules_applied)) rawRulesList.push(...metadata.rules_applied);
    }

    if (Array.isArray(metadata.guardrails_applied_details)) rawGuardrailsList.push(...metadata.guardrails_applied_details);
    if (Array.isArray(metadata.guardrails_applied)) rawGuardrailsList.push(...metadata.guardrails_applied);
    if (Array.isArray(metadata.guardrails)) rawGuardrailsList.push(...metadata.guardrails);
    if (isGuardrailBlocked && rawGuardrailsList.length === 0) {
      rawGuardrailsList.push({
        id: metadata.matched_item_id,
        title: metadata.matched_item_title || (metadata.category ? `Guardrail: ${metadata.category}` : "Guardrail Policy"),
        category: metadata.category || "safety",
        similarity_score: metadata.similarity_score,
        anchor_text: metadata.anchor_text,
        action: metadata.action || "block",
        severity: metadata.severity,
      });
    }

    if (Array.isArray(metadata.sources_consulted_details)) rawSourcesList.push(...metadata.sources_consulted_details);
    if (Array.isArray(quoteDraft?.sources_consulted_details)) rawSourcesList.push(...quoteDraft.sources_consulted_details);
    if (Array.isArray(metadata.draft?.sources_consulted_details)) rawSourcesList.push(...metadata.draft.sources_consulted_details);
    if (Array.isArray(metadata.sources_consulted)) rawSourcesList.push(...metadata.sources_consulted);
    if (Array.isArray(quoteDraft?.sources_consulted)) rawSourcesList.push(...quoteDraft.sources_consulted);
    if (Array.isArray(metadata.draft?.sources_consulted)) rawSourcesList.push(...metadata.draft.sources_consulted);
  }

  const resolvedRules = deduplicateRules(rawRulesList);
  const sourcesGroup = deduplicateAndGroupSources(rawSourcesList);
  const resolvedGuardrails = deduplicateGuardrails(rawGuardrailsList);

  const ruleIds = new Set(resolvedRules.filter((r) => r.id).map((r) => r.id!));
  const referencedRules = safeRules.filter((r) => ruleIds.has(r.id));

  const kIds = new Set(sourcesGroup.groups.filter((g) => !g.isTool && g.id).map((g) => g.id!));
  const referencedKnowledge = safeKnowledge.filter((k) => kIds.has(k.id));

  const gIds = new Set(resolvedGuardrails.filter((g) => g.id).map((g) => g.id!));
  const referencedGuardrails = safeGuardrails.filter((g) => gIds.has(g.id));

  return {
    referencedRules: referencedRules.length > 0 ? referencedRules : safeRules,
    referencedKnowledge: referencedKnowledge.length > 0 ? referencedKnowledge : safeKnowledge,
    referencedGuardrails: referencedGuardrails.length > 0 ? referencedGuardrails : safeGuardrails,
  };
}

export function formatChannelName(channelId?: string | null, metadata?: any): string {
  const metaChan =
    metadata?.channel_name ||
    metadata?.channel ||
    metadata?.source_channel_name ||
    metadata?.source_channel;
  if (metaChan && typeof metaChan === "string" && metaChan.trim().length > 0) {
    const trimmed = metaChan.trim();
    return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  }
  if (!channelId || !channelId.trim()) return "—";
  const trimmed = channelId.trim();
  if (trimmed.startsWith("#")) return trimmed;
  if (trimmed.startsWith("C") || trimmed.startsWith("G") || trimmed.startsWith("D")) {
    return `#${trimmed}`;
  }
  return trimmed;
}

export function formatCost(cost?: number | null): string {
  if (cost == null || isNaN(cost) || cost === 0) return "$0.00";
  if (cost < 0.0001) return "<$0.0001";
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(3)}`;
}

